import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { EntityCustomFields } from "@/shared/components/CustomFields";
import {
  UpdateDeviceTags,
  UpdateDeviceMetadata,
} from "@/shared/components/devices";
import { RobotoButton } from "@/shared/components/index.tsx";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { FormattedDateTime } from "@/shared/datetime";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";

import { useDeviceContext } from "../Device/useDeviceContext";

import { DeleteDeviceDialog } from "./DeleteDeviceDialog";

/**
 * Details tab of the device-detail route. Renders identifying fields
 * (id, created/modified timestamps and principals) plus inline editors
 * for tags, metadata, and custom fields.
 */
export function DeviceDetails() {
  const theme = useTheme();
  const { goto } = useNavigation();
  const { deviceId, org } = useDeviceContext();
  const currentOrgId = org?.org_id;
  const deviceQuery = useDevice(deviceId, currentOrgId);
  const { mutateAsync: updateDevice } = useUpdateDevice();
  const [customFieldsError, setCustomFieldsError] =
    React.useState<Error | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const device = deviceQuery.data;

  const dataLoading = deviceQuery.isLoading;

  return (
    <>
      {!device && dataLoading && (
        <Box marginTop={theme.spacing(2)}>
          <Skeleton variant="rounded" height="300px" />
        </Box>
      )}

      {deviceQuery.error && (
        <Box
          sx={{
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
          }}
        >
          <Alert severity="error">{deviceQuery.error.message}</Alert>
        </Box>
      )}

      {customFieldsError && (
        <Box sx={{ marginTop: theme.spacing(2) }}>
          <Alert severity="error" onClose={() => setCustomFieldsError(null)}>
            {customFieldsError.message}
          </Alert>
        </Box>
      )}

      {device && !dataLoading && (
        <Box>
          <List sx={{ width: "100%" }} disablePadding>
            <ListItem>
              <ListItemText
                primary={"Device ID"}
                secondary={device.device_id}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Created"}
                secondary={<FormattedDateTime value={device.created} />}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Created By"}
                secondary={
                  <RobotoPrincipalUnderlineLink
                    principal={device.created_by}
                    icon
                  />
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Modified"}
                secondary={<FormattedDateTime value={device.modified} />}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Modified By"}
                secondary={
                  <RobotoPrincipalUnderlineLink
                    principal={device.modified_by}
                    icon
                  />
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Tags"}
                secondary={
                  currentOrgId ? (
                    <UpdateDeviceTags
                      device={device}
                      resourceOwnerId={currentOrgId}
                    />
                  ) : (
                    "Loading..."
                  )
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Metadata"}
                secondary={
                  currentOrgId ? (
                    <UpdateDeviceMetadata
                      device={device}
                      resourceOwnerId={currentOrgId}
                    />
                  ) : (
                    "Loading..."
                  )
                }
              />
            </ListItem>
            <ListItem sx={{ display: "block" }}>
              <EntityCustomFields
                entityType={TargetEntityType.Device}
                orgId={device.org_id}
                values={device.custom_fields}
                commit={(changeset) =>
                  updateDevice({
                    deviceId,
                    resourceOwnerId: device.org_id,
                    updates: { custom_fields_changeset: changeset },
                  })
                }
                onError={setCustomFieldsError}
                variant="plain"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Operations"}
                secondaryTypographyProps={{ component: "span" }}
                secondary={
                  <Box sx={{ marginTop: theme.spacing(1) }}>
                    <RobotoButton
                      eventName="DeleteDeviceClicked"
                      eventProperties={{ deviceId }}
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlinedIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete Device
                    </RobotoButton>
                  </Box>
                }
              />
            </ListItem>
          </List>

          <DeleteDeviceDialog
            device={deleteDialogOpen ? device : null}
            onClose={() => setDeleteDialogOpen(false)}
            onDeleted={() => {
              setDeleteDialogOpen(false);
              goto.devices();
            }}
          />
        </Box>
      )}
    </>
  );
}
